import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import type { Heat } from '../../../types/heats';

/**
 * Statuts posables depuis un écran de PLANIFICATION.
 *
 * `preparing`, `countdown` et `running` en sont absents à dessein : ce sont des
 * états d'exécution, écrits par le lancement de heat et l'app Juge pendant
 * l'événement. Les proposer ici reviendrait à laisser un écran de préparation
 * écraser ce que le terrain est en train de dire.
 */
export const BULK_STATUSES = ['scheduled', 'ready', 'cancelled'] as const;

/** Statuts qu'on ne touche pas : une heat en cours, ou finie avec ses résultats. */
const VERROUILLES = new Set(['preparing', 'countdown', 'running', 'finished', 'completed']);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heats: Heat[];
  wodName: (heat: Heat) => string;
  categoryName: (heat: Heat) => string;
  loading: boolean;
  /** Événement passé : le verrou tombe et « Terminé » devient posable (2026-09-27). */
  eventOver?: boolean;
  onConfirm: (heatIds: string[], status: string) => void;
}

/**
 * Changement de statut en lot.
 *
 * Corriger un statut heat par heat est le genre de tâche qu'on abandonne à
 * mi-chemin. Les heats verrouillées restent VISIBLES mais non cochables :
 * les masquer ferait croire à un planning plus court qu'il n'est, et
 * l'organisateur se demanderait où sont passées ses heats terminées.
 */
const BulkStatusDialog: React.FC<Props> = ({
  open, onOpenChange, heats, wodName, categoryName, loading, eventOver = false, onConfirm,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState<string>('ready');
  const [selected, setSelected] = React.useState<string[]>([]);

  // Rien de pré-coché : changer un statut est une action délibérée, et une
  // sélection héritée de la dernière ouverture serait une chausse-trape.
  React.useEffect(() => { if (open) setSelected([]); }, [open]);

  const estVerrouillee = React.useCallback(
    (h: Heat) => !eventOver && VERROUILLES.has((h.status || '').toLowerCase()),
    [eventOver],
  );
  const statuts: readonly string[] = eventOver ? [...BULK_STATUSES, 'finished'] : BULK_STATUSES;
  const modifiables = React.useMemo(() => heats.filter(h => !estVerrouillee(h)), [heats, estVerrouillee]);
  const toutCoche = modifiables.length > 0 && selected.length === modifiables.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t('heats.bulkStatus.title')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t('heats.bulkStatus.description')}</p>
        {eventOver && <p className="text-sm text-muted-foreground">{t('heats.bulkStatus.eventOver')}</p>}

        <div className="flex items-center gap-3">
          <span className="text-sm">{t('heats.bulkStatus.newStatus')}</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statuts.map(s => (
                <SelectItem key={s} value={s}>{t(`heats.statusLabels.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="max-h-[45vh] overflow-y-auto rounded border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={toutCoche}
                    onCheckedChange={() => setSelected(toutCoche ? [] : modifiables.map(h => h.id))}
                    aria-label={t('heats.bulkStatus.selectAll')}
                  />
                </TableHead>
                <TableHead>{t('heats.table.heat')}</TableHead>
                <TableHead>{t('heats.table.wod')}</TableHead>
                <TableHead>{t('heats.table.category')}</TableHead>
                <TableHead>{t('heats.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {heats.map(heat => {
                const verrouille = estVerrouillee(heat);
                const coche = selected.includes(heat.id);
                return (
                  <TableRow
                    key={heat.id}
                    className={cn(verrouille && 'opacity-40')}
                    title={verrouille ? t('heats.bulkStatus.lockedHint') : undefined}
                  >
                    <TableCell>
                      <Checkbox
                        checked={coche}
                        disabled={verrouille}
                        onCheckedChange={() => setSelected(prev =>
                          prev.includes(heat.id)
                            ? prev.filter(id => id !== heat.id)
                            : [...prev, heat.id])}
                      />
                    </TableCell>
                    <TableCell className="font-semibold tabular-nums">
                      #{heat.heat_number}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {heat.start_time?.slice(0, 5)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{wodName(heat)}</TableCell>
                    <TableCell className="text-sm">{categoryName(heat)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t(`heats.statusLabels.${heat.status}`, { defaultValue: heat.status })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={selected.length === 0 || loading}
            onClick={() => onConfirm(selected, status)}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('heats.bulkStatus.apply', { count: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStatusDialog;
